import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { API } from "@/App";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Repeat, CalendarRange, Users, TrendingUp, TrendingDown, AlertCircle, UserPlus, UserMinus, Activity, Percent, FileDown
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, ReferenceLine, Legend } from "recharts";

const formatAUD = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n || 0);
const formatAUDPrecise = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n || 0);

const cardWidth = "w-52";

const HeroCard = ({ icon: Icon, label, value, sub, accent = "slate" }) => {
  const accentMap = {
    slate: { title: "text-slate-500", value: "text-slate-900" },
    emerald: { title: "text-emerald-600", value: "text-emerald-600" },
    indigo: { title: "text-indigo-600", value: "text-indigo-600" },
    red: { title: "text-red-500", value: "text-red-600" },
    amber: { title: "text-amber-600", value: "text-amber-700" },
  };
  const a = accentMap[accent] || accentMap.slate;
  return (
    <Card className={`bg-white ${cardWidth}`}>
      <CardHeader className="pb-2 px-4 pt-4">
        <CardTitle className={`text-sm font-medium ${a.title} flex items-center gap-2`}>
          <Icon className="w-4 h-4" />{label}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 text-center">
        <p className={`text-2xl font-bold ${a.value}`}>{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
};

const SalesDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("12mo");
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);
  const token = localStorage.getItem("token");

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("token");
    navigate("/");
  };

  // Capture the live dashboard as a multi-page PDF using html2canvas + jsPDF.
  // This avoids the print-stylesheet chart re-measurement problem entirely —
  // the canvas snapshot is taken from the working browser DOM where Recharts
  // has already laid everything out correctly.
  const handleExportPDF = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: "#f8fafc",
        useCORS: true,
        logging: false,
      });

      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;

      // Scale image so its width fills the usable page width
      const imgWidth = usableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Slice the long image into multiple pages by translating its Y origin
      let remainingHeight = imgHeight;
      let position = 0;
      const imgData = canvas.toDataURL("image/png");

      while (remainingHeight > 0) {
        pdf.addImage(imgData, "PNG", margin, margin - position, imgWidth, imgHeight);
        remainingHeight -= usableHeight;
        if (remainingHeight > 0) {
          pdf.addPage();
          position += usableHeight;
        }
      }

      const stamp = new Date().toISOString().slice(0, 10);
      pdf.save(`storm-pad-sales-report-${stamp}.pdf`);
    } catch (err) {
      console.error("PDF export failed:", err);
      toast.error("PDF export failed. See console for details.");
    } finally {
      setExporting(false);
    }
  };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sales-dashboard?range=${range}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        setUser(null);
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [token, range, setUser, navigate]);

  useEffect(() => {
    if (user?.role !== "manager") {
      navigate("/leads");
      return;
    }
    fetchDashboard();
  }, [user, navigate, fetchDashboard]);

  const hero = data?.hero || {};
  const arrSeries = (data?.arr_over_time || []).map((p) => ({ ...p, label: p.month }));

  const deltaArrow = (pct) => {
    if (pct === null || pct === undefined) return null;
    const Up = TrendingUp, Down = TrendingDown;
    const Icon = pct >= 0 ? Up : Down;
    const color = pct >= 0 ? "text-emerald-600" : "text-red-600";
    return <span className={`inline-flex items-center gap-1 ${color}`}><Icon className="w-3 h-3" />{pct > 0 ? "+" : ""}{pct}%</span>;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="sales-dashboard-page">
      <Sidebar user={user} setUser={setUser} onLogout={handleLogout} />
      <main className="flex-1 p-8 ml-64">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Sales Dashboard</h1>
            <p className="text-slate-500 mt-1">Investor-grade view of recurring revenue, growth, and churn</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="12mo">Last 12 months</SelectItem>
                <SelectItem value="all_time">All time</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={handleExportPDF}
              variant="outline"
              className="gap-2"
              disabled={exporting}
              data-testid="export-pdf-btn"
            >
              <FileDown className="w-4 h-4" />
              {exporting ? "Generating..." : "Export PDF"}
            </Button>
          </div>
        </div>

        {loading && <div className="text-slate-500">Loading…</div>}

        {!loading && data && (
          <div ref={reportRef}>
            {/* Inline report header — visible in PDF only (hidden in browser via CSS class) */}
            <div className="hidden print-only mb-6 pb-4 border-b border-slate-300" style={exporting ? { display: "block" } : undefined}>
              <h1 className="text-2xl font-bold text-slate-900">Sales Report</h1>
              <p className="text-sm text-slate-600">{user?.org_name || ""} · Generated {new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</p>
              <p className="text-xs text-slate-500 mt-1">Range: {range === "12mo" ? "Last 12 months" : "All time"}</p>
            </div>

            <div className="flex flex-wrap gap-3 mb-8">
              <HeroCard
                icon={CalendarRange}
                label="ARR"
                value={formatAUD(hero.arr)}
                sub={deltaArrow(hero.arr_delta_pct) || "—"}
                accent="indigo"
              />
              <HeroCard
                icon={Repeat}
                label="MRR"
                value={formatAUD(hero.mrr)}
                accent="indigo"
              />
              <HeroCard
                icon={Users}
                label="Active Customers"
                value={hero.active_customers || 0}
                sub={`${hero.pending_count || 0} pending`}
              />
              <HeroCard
                icon={UserPlus}
                label="New Sales (12mo)"
                value={hero.new_subs_12mo || 0}
                sub={formatAUD(hero.new_arr_12mo)}
                accent="emerald"
              />
              <HeroCard
                icon={UserMinus}
                label="Churned (12mo)"
                value={hero.churn_count_12mo || 0}
                sub={formatAUD(hero.churn_arr_12mo)}
                accent="red"
              />
              <HeroCard
                icon={Activity}
                label="Net New ARR (12mo)"
                value={formatAUD(hero.net_new_arr_12mo)}
                accent={hero.net_new_arr_12mo >= 0 ? "emerald" : "red"}
              />
              <HeroCard
                icon={Percent}
                label="Churn Rate (12mo)"
                value={`${hero.churn_rate_12mo_pct ?? 0}%`}
                accent={hero.churn_rate_12mo_pct > 10 ? "red" : "slate"}
              />
              <HeroCard
                icon={TrendingUp}
                label="NRR"
                value={hero.nrr_pct !== null && hero.nrr_pct !== undefined ? `${hero.nrr_pct}%` : "—"}
                sub={hero.nrr_pct >= 100 ? "Expanding" : "Contracting"}
                accent={hero.nrr_pct >= 100 ? "emerald" : "red"}
              />
            </div>

            <Card className="bg-white mb-8">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-700">ARR over time</CardTitle>
                <p className="text-xs text-slate-500">Reconstructed from paid renewal history. Approximation until monthly snapshots accumulate.</p>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={arrSeries} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
                      <YAxis
                        tick={{ fontSize: 12, fill: "#64748b" }}
                        tickFormatter={(v) => formatAUD(v)}
                        width={90}
                      />
                      <Tooltip
                        formatter={(v) => formatAUDPrecise(v)}
                        contentStyle={{ fontSize: 12 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="arr"
                        stroke="#6366f1"
                        strokeWidth={2}
                        dot={{ r: 3, fill: "#6366f1" }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white mb-8">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-700">Active customers over time</CardTitle>
                <p className="text-xs text-slate-500">Count of paying customers (excludes FREE/CANCELLED) at the end of each month, reconstructed from paid history.</p>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={arrSeries} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
                      <YAxis tick={{ fontSize: 12, fill: "#64748b" }} width={60} allowDecimals={false} />
                      <Tooltip formatter={(v) => [v, "Customers"]} contentStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="customers" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3, fill: "#0ea5e9" }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white mb-8">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-700">New ARR vs Churn ARR by month</CardTitle>
                <p className="text-xs text-slate-500">Green = new ARR added · Red = ARR lost to churn · Net is the difference</p>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.monthly_changes || []} margin={{ top: 10, right: 20, left: 10, bottom: 0 }} stackOffset="sign">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} />
                      <YAxis tick={{ fontSize: 12, fill: "#64748b" }} tickFormatter={(v) => formatAUD(v)} width={90} />
                      <Tooltip formatter={(v, name) => [formatAUDPrecise(Math.abs(v)), name === "new_arr" ? "New ARR" : "Churn ARR"]} contentStyle={{ fontSize: 12 }} />
                      <ReferenceLine y={0} stroke="#94a3b8" />
                      <Legend formatter={(v) => v === "new_arr" ? "New ARR" : "Churn ARR"} wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="new_arr" stackId="stack" fill="#10b981" />
                      <Bar dataKey="churn_arr" stackId="stack" fill="#ef4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white mb-8">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-700">Cohort retention</CardTitle>
                <p className="text-xs text-slate-500">% of paid customers from each sign-up quarter still paying after N years. "—" means cohort hasn't reached that age yet.</p>
              </CardHeader>
              <CardContent>
                {(data.cohort_retention || []).length === 0 ? (
                  <p className="text-sm text-slate-400">No paid customers yet to form cohorts.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left border-b border-slate-200">
                          <th className="py-2 pr-4 font-medium text-slate-500">Cohort</th>
                          <th className="py-2 pr-4 font-medium text-slate-500 text-right">Signed up</th>
                          <th className="py-2 pr-4 font-medium text-slate-500 text-right">Year 1</th>
                          <th className="py-2 pr-4 font-medium text-slate-500 text-right">Year 2</th>
                          <th className="py-2 pr-4 font-medium text-slate-500 text-right">Year 3</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.cohort_retention || []).map((c) => (
                          <tr key={c.cohort} className="border-b border-slate-100">
                            <td className="py-2 pr-4 text-slate-700">{c.cohort}</td>
                            <td className="py-2 pr-4 text-right text-slate-700">{c.signed_up}</td>
                            <td className="py-2 pr-4 text-right">{c.y1_pct === null ? <span className="text-slate-300">—</span> : <span className={c.y1_pct >= 80 ? "text-emerald-600 font-medium" : c.y1_pct >= 50 ? "text-amber-600" : "text-red-600"}>{c.y1_pct}%</span>}</td>
                            <td className="py-2 pr-4 text-right">{c.y2_pct === null ? <span className="text-slate-300">—</span> : <span className={c.y2_pct >= 80 ? "text-emerald-600 font-medium" : c.y2_pct >= 50 ? "text-amber-600" : "text-red-600"}>{c.y2_pct}%</span>}</td>
                            <td className="py-2 pr-4 text-right">{c.y3_pct === null ? <span className="text-slate-300">—</span> : <span className={c.y3_pct >= 80 ? "text-emerald-600 font-medium" : c.y3_pct >= 50 ? "text-amber-600" : "text-red-600"}>{c.y3_pct}%</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white mb-8">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-700">Pipeline (next 90 days)</CardTitle>
                <p className="text-xs text-slate-500">Upcoming unpaid renewals broken down by horizon and likeliness, plus PENDING subs awaiting first payment.</p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-slate-200">
                        <th className="py-2 pr-4 font-medium text-slate-500">Horizon</th>
                        <th className="py-2 pr-4 font-medium text-slate-500 text-right">YES</th>
                        <th className="py-2 pr-4 font-medium text-slate-500 text-right">MIGHT</th>
                        <th className="py-2 pr-4 font-medium text-slate-500 text-right">NOT</th>
                        <th className="py-2 pr-4 font-medium text-slate-500 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {["30", "60", "90"].map((h) => {
                        const row = data.pipeline?.[`renewals_${h}d`] || {};
                        const total = (row.YES?.value || 0) + (row.MIGHT?.value || 0) + (row.NOT?.value || 0);
                        const totalCount = (row.YES?.count || 0) + (row.MIGHT?.count || 0) + (row.NOT?.count || 0);
                        return (
                          <tr key={h} className="border-b border-slate-100">
                            <td className="py-2 pr-4 text-slate-700">Next {h} days</td>
                            <td className="py-2 pr-4 text-right text-emerald-700">{row.YES?.count || 0} · {formatAUD(row.YES?.value || 0)}</td>
                            <td className="py-2 pr-4 text-right text-amber-700">{row.MIGHT?.count || 0} · {formatAUD(row.MIGHT?.value || 0)}</td>
                            <td className="py-2 pr-4 text-right text-red-700">{row.NOT?.count || 0} · {formatAUD(row.NOT?.value || 0)}</td>
                            <td className="py-2 pr-4 text-right font-medium text-slate-900">{totalCount} · {formatAUD(total)}</td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td className="py-2 pr-4 text-slate-700">PENDING (first-payment)</td>
                        <td className="py-2 pr-4 text-right text-sky-700" colSpan="3">{data.pipeline?.pending_count || 0} · {formatAUD(data.pipeline?.pending_value || 0)}</td>
                        <td className="py-2 pr-4 text-right text-sky-700"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white mb-8">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-700">Forecasted ARR — end of next quarter</CardTitle>
                <p className="text-xs text-slate-500">Bear: only YES renews, no new sales. Base: YES+MIGHT renew, average new sales (trailing 12mo). Bull: YES+MIGHT + 50% NOT renew, 1.5× new sales.</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg border border-red-200 bg-red-50">
                    <p className="text-xs font-medium text-red-600 uppercase">Bear</p>
                    <p className="text-2xl font-bold text-red-700 mt-1">{formatAUD(data.forecast?.bear_arr)}</p>
                    <p className="text-xs text-red-500 mt-1">vs current {formatAUD(data.forecast?.current_arr)}</p>
                  </div>
                  <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
                    <p className="text-xs font-medium text-slate-600 uppercase">Base</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{formatAUD(data.forecast?.base_arr)}</p>
                    <p className="text-xs text-slate-500 mt-1">+ {formatAUD(data.pipeline?.new_sales_per_quarter_avg)} avg new/qtr</p>
                  </div>
                  <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50">
                    <p className="text-xs font-medium text-emerald-600 uppercase">Bull</p>
                    <p className="text-2xl font-bold text-emerald-700 mt-1">{formatAUD(data.forecast?.bull_arr)}</p>
                    <p className="text-xs text-emerald-500 mt-1">all renews + 1.5× new sales</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white mb-8">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-700">Forecasted next 12 months</CardTitle>
                <p className="text-xs text-slate-500">Existing book × NRR + projected new sales (trailing 12-month new ARR baseline). Bear/Base/Bull adjust both NRR and new-sales pace.</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg border border-red-200 bg-red-50">
                    <p className="text-xs font-medium text-red-600 uppercase">Bear</p>
                    <p className="text-2xl font-bold text-red-700 mt-1">{formatAUD(data.forecast_12mo?.bear_arr)}</p>
                    <p className="text-xs text-red-500 mt-1">NRR {data.forecast_12mo?.nrr_bear_pct}% · new sales 0.5×</p>
                  </div>
                  <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
                    <p className="text-xs font-medium text-slate-600 uppercase">Base</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{formatAUD(data.forecast_12mo?.base_arr)}</p>
                    <p className="text-xs text-slate-500 mt-1">NRR {data.forecast_12mo?.nrr_base_pct}% · new sales 1× ({formatAUD(data.forecast_12mo?.new_arr_12mo_baseline)})</p>
                  </div>
                  <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50">
                    <p className="text-xs font-medium text-emerald-600 uppercase">Bull</p>
                    <p className="text-2xl font-bold text-emerald-700 mt-1">{formatAUD(data.forecast_12mo?.bull_arr)}</p>
                    <p className="text-xs text-emerald-500 mt-1">NRR {data.forecast_12mo?.nrr_bull_pct}% · new sales 1.25×</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white mb-8">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-700">Risk panel</CardTitle>
                <p className="text-xs text-slate-500">At-risk ARR (subs marked MIGHT/NOT) and customer concentration (top 5 by current rate).</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 rounded-lg border border-amber-200 bg-amber-50">
                    <p className="text-xs font-medium text-amber-600 uppercase">At risk — MIGHT</p>
                    <p className="text-2xl font-bold text-amber-700 mt-1">{formatAUD(data.risk?.at_risk_might_arr)}</p>
                  </div>
                  <div className="p-4 rounded-lg border border-red-200 bg-red-50">
                    <p className="text-xs font-medium text-red-600 uppercase">At risk — NOT</p>
                    <p className="text-2xl font-bold text-red-700 mt-1">{formatAUD(data.risk?.at_risk_not_arr)}</p>
                  </div>
                  <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
                    <p className="text-xs font-medium text-slate-600 uppercase">Top 5 customer concentration</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{data.risk?.top5_concentration_pct || 0}%</p>
                    <p className="text-xs text-slate-500 mt-1">{(data.risk?.top5_concentration_pct || 0) > 30 ? "Investors flag >30%" : "Healthy spread"}</p>
                  </div>
                </div>
                <p className="text-sm font-medium text-slate-600 mb-2">Top 5 customers by current ARR</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-slate-200">
                        <th className="py-2 pr-4 font-medium text-slate-500">Customer</th>
                        <th className="py-2 pr-4 font-medium text-slate-500 text-right">Current ARR</th>
                        <th className="py-2 pr-4 font-medium text-slate-500 text-right">% of total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.risk?.top5_concentration || []).map((c) => (
                        <tr key={c.business_name} className="border-b border-slate-100">
                          <td className="py-2 pr-4 text-slate-700">{c.business_name}</td>
                          <td className="py-2 pr-4 text-right text-slate-700">{formatAUDPrecise(c.arr)}</td>
                          <td className="py-2 pr-4 text-right text-slate-700">{c.pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white mb-8">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-700">Pricing power</CardTitle>
                <p className="text-xs text-slate-500">Average new-customer price over time, average price by renewal year (the "land and expand" story), and per-customer price growth.</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-6 mb-6">
                  <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50">
                    <p className="text-xs font-medium text-emerald-600 uppercase">Avg per-customer price growth</p>
                    <p className="text-3xl font-bold text-emerald-700 mt-1">{(data.pricing?.avg_price_increase_pct ?? 0) > 0 ? "+" : ""}{data.pricing?.avg_price_increase_pct ?? 0}%</p>
                    <p className="text-xs text-emerald-600 mt-1">Initial → most recent paid renewal · {data.pricing?.customers_with_renewal_history || 0} customers</p>
                  </div>
                  <div className="p-4 rounded-lg border border-indigo-200 bg-indigo-50">
                    <p className="text-xs font-medium text-indigo-600 uppercase">Avg new sale</p>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <div>
                        <p className="text-xs text-indigo-500">3mo</p>
                        <p className="text-lg font-bold text-indigo-700">{formatAUD(data.pricing?.avg_new_price_3mo)}</p>
                        <p className="text-xs text-indigo-400">n={data.pricing?.new_sales_3mo_count || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-indigo-500">6mo</p>
                        <p className="text-lg font-bold text-indigo-700">{formatAUD(data.pricing?.avg_new_price_6mo)}</p>
                        <p className="text-xs text-indigo-400">n={data.pricing?.new_sales_6mo_count || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-indigo-500">12mo</p>
                        <p className="text-lg font-bold text-indigo-700">{formatAUD(data.pricing?.avg_new_price_12mo)}</p>
                        <p className="text-xs text-indigo-400">n={data.pricing?.new_sales_12mo_count || 0}</p>
                      </div>
                    </div>
                    <p className="text-xs text-indigo-600 mt-2">
                      {(data.pricing?.new_price_uplift_pct ?? 0) > 0 ? "+" : ""}{data.pricing?.new_price_uplift_pct ?? 0}% (12mo vs all-time avg {formatAUD(data.pricing?.avg_new_price_all_time)})
                    </p>
                  </div>
                  <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
                    <p className="text-xs font-medium text-slate-600 uppercase">Avg price by renewal year</p>
                    <div className="grid grid-cols-4 gap-2 mt-2">
                      {(data.pricing?.by_renewal_year || []).map((row) => (
                        <div key={row.label}>
                          <p className="text-xs text-slate-500">{row.label}</p>
                          <p className="text-lg font-bold text-slate-900">{formatAUD(row.avg_price)}</p>
                          <p className="text-xs text-slate-400">n={row.count}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <p className="text-sm font-medium text-slate-600 mb-2">Avg new-customer price by quarter</p>
                {(data.pricing?.avg_new_price_by_quarter || []).length === 0 ? (
                  <p className="text-sm text-slate-400">Not enough new-customer history yet.</p>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.pricing.avg_new_price_by_quarter} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="quarter" tick={{ fontSize: 12, fill: "#64748b" }} />
                        <YAxis tick={{ fontSize: 12, fill: "#64748b" }} tickFormatter={(v) => formatAUD(v)} width={90} />
                        <Tooltip
                          formatter={(v, _, props) => [formatAUDPrecise(v), `Avg price · ${props.payload.count} new customers`]}
                          contentStyle={{ fontSize: 12 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="avg_price"
                          stroke="#10b981"
                          strokeWidth={2}
                          dot={{ r: 3, fill: "#10b981" }}
                          activeDot={{ r: 5 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            <p className="text-xs text-slate-400 print:hide">Sales-by-rep deferred — subscriptions don't track which rep closed them yet. Use Export PDF to save this view as an investor report.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default SalesDashboard;
